import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiAward, FiStar, FiClock, FiCheckCircle, FiTarget, FiTrendingUp, FiBook, FiLogOut } = FiIcons;

const Profile = ({ user, setCurrentSection, onLogout }) => {
  // Guard clause if user is undefined (though protected route handles this, safe to keep)
  if (!user) return null;

  const completedCount = Object.values(user.progress || {}).filter(Boolean).length;
  const totalModules = Object.keys(user.progress || {}).length || 1; // avoid divide by zero
  const progressPercent = Math.round((completedCount / totalModules) * 100);

  const getLevel = () => {
    if (progressPercent === 100) return 'Amharic Master';
    if (progressPercent >= 75) return 'Advanced Learner';
    if (progressPercent >= 50) return 'Intermediate Speaker';
    return 'Beginner';
  };

  const achievements = [
    { id: 'start', title: 'First Steps', desc: 'Started the journey', icon: FiUser, unlocked: true },
    { id: 'half', title: 'Halfway There', desc: 'Completed 50% of modules', icon: FiTrendingUp, unlocked: progressPercent >= 50 },
    { id: 'quiz', title: 'Quiz Master', desc: 'Passed the final assessment', icon: FiStar, unlocked: user.progress?.reflection },
    { id: 'done', title: 'Graduate', desc: 'Completed all modules', icon: FiAward, unlocked: progressPercent === 100 },
  ];

  return (
    <PageLayout
      title="Student Profile"
      subtitle="Track your progress, view achievements, and manage your learning journey."
      icon={FiUser}
      color="text-blue-600"
      bgColor="bg-blue-50"
    >
      <div className="space-y-8 fade-in">
        
        {/* User Stats Card */}
        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-lg flex flex-col md:flex-row items-center gap-8 relative overflow-hidden">
          <div className="relative z-10">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center text-6xl shadow-xl border-4 border-white">
              {user.avatar}
            </div>
            <div className="absolute bottom-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full border-2 border-white shadow-sm">
              Level {Math.floor(progressPercent / 20) + 1}
            </div>
          </div>
          
          <div className="flex-1 text-center md:text-left relative z-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-1">{user.name}</h2>
            <p className="text-blue-600 font-medium mb-4 flex items-center justify-center md:justify-start gap-2">
              <SafeIcon icon={FiAward} />
              {getLevel()}
            </p>
            
            <div className="flex flex-wrap justify-center md:justify-start gap-4">
              <div className="bg-gray-50 px-4 py-2 rounded-xl text-center border border-gray-200">
                <span className="block text-xl font-bold text-gray-800">{completedCount}/{totalModules}</span>
                <span className="text-xs text-gray-500 uppercase tracking-wider">Modules</span>
              </div>
              <div className="bg-gray-50 px-4 py-2 rounded-xl text-center border border-gray-200">
                <span className="block text-xl font-bold text-gray-800">{user.xp || 0}</span>
                <span className="text-xs text-gray-500 uppercase tracking-wider">XP Earned</span>
              </div>
              <div className="bg-gray-50 px-4 py-2 rounded-xl text-center border border-gray-200">
                <span className="block text-xl font-bold text-gray-800">{user.streak || 0}</span>
                <span className="text-xs text-gray-500 uppercase tracking-wider">Day Streak</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 relative z-10">
            {progressPercent === 100 && (
              <Link 
                to="/certificate"
                className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all flex items-center gap-2 justify-center"
              >
                <SafeIcon icon={FiAward} />
                View Certificate
              </Link>
            )}
            <button 
              onClick={onLogout}
              className="bg-white border border-red-200 text-red-600 px-6 py-3 rounded-xl font-bold hover:bg-red-50 transition-all flex items-center gap-2 justify-center"
            >
              <SafeIcon icon={FiLogOut} />
              Logout / Switch User
            </button>
          </div>

          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-50 to-purple-50 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2"></div>
        </div>

        {/* Progress Overview */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Module Progress */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <SafeIcon icon={FiBook} className="text-blue-500" />
              Module Progress
            </h3>
            <div className="space-y-4">
              {Object.entries(user.progress || {}).map(([key, completed]) => (
                <div key={key} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${completed ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                    <SafeIcon icon={completed ? FiCheckCircle : FiTarget} />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium capitalize text-gray-700">{key}</span>
                      <span className="text-xs text-gray-500">{completed ? 'Completed' : 'Pending'}</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${completed ? 'bg-green-500 w-full' : 'bg-gray-300 w-0'} transition-all duration-500`}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Achievements */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <SafeIcon icon={FiAward} className="text-yellow-500" />
              Achievements
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {achievements.map((ach) => (
                <div key={ach.id} className={`p-4 rounded-xl border-2 text-center transition-all ${ach.unlocked ? 'border-yellow-200 bg-yellow-50' : 'border-gray-100 bg-gray-50 opacity-60 grayscale'}`}>
                  <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2 ${ach.unlocked ? 'bg-white text-yellow-500 shadow-sm' : 'bg-gray-200 text-gray-400'}`}>
                    <SafeIcon icon={ach.icon} className="text-xl" />
                  </div>
                  <h4 className="font-bold text-gray-800 text-sm mb-1">{ach.title}</h4>
                  <p className="text-xs text-gray-500">{ach.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </PageLayout>
  );
};

export default Profile;